import isNil from 'lodash/isNil'
import moment, {type Moment} from 'moment-timezone'
import {v4 as uuidv4} from 'uuid'
import type {ParserT} from '@freckle/parser'
import {any, date, firstOf, mapStatic, nullable, Parser, string} from '@freckle/parser'
import {
  ActionableHttpError,
  ActionableUnknownError,
  NonActionableHttpError,
  ClientMalformedRequestError,
  ClientNetworkError,
  ClientRequestAbortError,
} from './errors'
import {isNetworkError} from './is-network-error'
import {updateClientErrorStack as updateErrorStack} from './update-client-error-stack'

const REQUEST_ECHO = uuidv4()

const freckleFetch = async (
  input: string | URL | Request,
  init?: RequestInit & {excludeEcho?: boolean; timeout?: number},
) => {
  const {excludeEcho = false, timeout, ...fetchInit} = init ?? {}

  const controller = new AbortController()
  if (timeout !== undefined) {
    setTimeout(() => controller.abort(), timeout)
  }

  const request = new Request(input, {
    credentials: 'include',
    signal: controller.signal,
    ...fetchInit,
  })

  const originalStack = new Error().stack

  let urlWithXEcho: URL | undefined = undefined
  try {
    urlWithXEcho = new URL(request.url)
    urlWithXEcho.searchParams.delete('x-echo')
    urlWithXEcho.searchParams.append('x-echo', REQUEST_ECHO)
  } catch (_e) {}

  try {
    const req =
      urlWithXEcho && !excludeEcho
        ? new Request(urlWithXEcho, {
            method: request.method,
            keepalive: request.keepalive,
            headers: request.headers,
            body: ['GET', 'HEAD'].includes(request.method) ? undefined : await request.text(),
            redirect: request.redirect,
            integrity: request.integrity,
            signal: request.signal,
            credentials: request.credentials,
            mode: request.mode,
            referrer: request.referrer,
            referrerPolicy: request.referrerPolicy,
          })
        : request

    const response = await window.fetch(req)

    if (response.ok) return response

    const {status, headers} = response
    const {method, url} = request

    const errorConstructor =
      [400, 401, 403].includes(status) || status >= 500
        ? NonActionableHttpError
        : ActionableHttpError

    const contentType = headers.get('content-type')
    if (contentType !== null && contentType.includes('application/json')) {
      throw new errorConstructor(url, method, response.clone().status, {
        response: response.clone(),
        json: await response.clone().json(),
      })
    } else {
      // API returned unexpected non 2xx response code
      throw new errorConstructor(url, method, response.clone().status, {
        response: response.clone(),
        text: await response.clone().text(),
      })
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      // request aborted via AbortController's abort()
      throw updateErrorStack(new ClientRequestAbortError(e.message, e), originalStack)
    }
    if (isNetworkError(e)) {
      // Network issues preventing client to make the request
      throw updateErrorStack(new ClientNetworkError(e.message, e), originalStack)
    }
    if (e instanceof TypeError) {
      // The supplied Fetch options/url are invalid
      throw updateErrorStack(new ClientMalformedRequestError(e.message, e), originalStack)
    }
    // The error thrown inside if(!response.ok)
    if (e instanceof ActionableHttpError || e instanceof NonActionableHttpError) {
      throw updateErrorStack(e, originalStack)
    }

    if (e instanceof Error) {
      throw updateErrorStack(
        new ActionableUnknownError(`${e.name}: ${e.message}`, e),
        originalStack,
      )
    }

    throw updateErrorStack(new ActionableUnknownError('Unexpected error', e), originalStack)
  }
}

export const fancyApiFetch = freckleFetch

export const ttsApiFetch = freckleFetch

export const curriculaApiFetch = async (
  input: string | URL | Request,
  init?: RequestInit & {timeout?: number},
) => freckleFetch(input, {...init, excludeEcho: true})

export type OperationLocationT = string

export type RetryAfterT =
  | {
      tag: 'http-date'
      contents: Moment
    }
  | {
      tag: 'delay-seconds'
      contents: number
    }

// N.B.: Currently we're only using delay-seconds; extra work is required to distinguish between date and string
const parseRetryAfter = firstOf<RetryAfterT>(
  mapStatic(string(), delaySeconds => ({
    tag: 'delay-seconds',
    contents: parseInt(delaySeconds, 10),
  })),
  mapStatic(mapStatic(date(), moment), httpDate => ({tag: 'http-date', contents: httpDate})),
)

type ResponseWithRetryAfter<T> = {
  response: T
  retryAfter: RetryAfterT | undefined | null
}

export const fancyApiFetchWithRetry = <T>(
  url: OperationLocationT,
  responseParser?: ParserT<T>,
): Promise<ResponseWithRetryAfter<T>> =>
  freckleFetch(url).then(async res => {
    const retryAfter = res.headers.get('Retry-After')
    const response = await res.json()
    return {
      response: Parser.run(response, responseParser ?? any()),
      retryAfter: Parser.run(retryAfter, nullable(parseRetryAfter)),
    }
  })

export type LinkName = 'first' | 'previous' | 'next' | 'last'
export type LinkPathT = string

export type LinksT = {
  [name in LinkName]: LinkPathT
}

const toLinkName = (rawName: string): LinkName => {
  switch (rawName) {
    case 'first':
    case 'previous':
    case 'next':
    case 'last':
      return rawName
    default:
      throw new Error(`Could not parse ${rawName}`)
  }
}

export function parseLinkHeader(linkHeader: string | null): LinksT {
  if (isNil(linkHeader) || linkHeader.trim().length === 0) {
    throw new Error('Expected non-zero Link header')
  }

  // Split parts by comma
  const parts = linkHeader.split(',')
  const links = {} as LinksT

  // Parse each part into a named link
  parts.forEach(part => {
    const section = part.split(';')
    if (section.length !== 2) {
      return
    }

    const url = section[0]!.replace(/<(.*)>/, '$1').trim()
    const rawName = section[1]!.replace(/rel="(.*)"/, '$1').trim()
    const name = toLinkName(rawName)
    links[name] = url
  })
  return links
}

export function parseOperationLocationHeader(header: string | null): string {
  if (header === null || header.trim().length === 0) {
    throw new Error('Expected Operation-Location header')
  }
  return header
}
